import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Globe, Clock, Star, Filter, Search, ExternalLink, Heart, Users } from 'lucide-react';

interface LocalResource {
  id: string;
  name: string;
  type: 'government' | 'nonprofit' | 'community' | 'business' | 'healthcare' | 'education';
  description: string;
  address: string;
  phone?: string;
  website?: string;
  hours: string;
  rating: number;
  distance: number; // in miles
  languages: string[];
  services: string[];
  eligibility: string[];
  cost: 'free' | 'low-cost' | 'sliding-scale' | 'paid';
  isVerified: boolean;
  lastUpdated: Date;
}

interface LocalResourceFinderProps {
  userLocation?: {
    city: string;
    state: string;
    zipCode: string;
  };
  onResourceSelect?: (resource: LocalResource) => void;
}

const LocalResourceFinder: React.FC<LocalResourceFinderProps> = ({
  userLocation = { city: 'Los Angeles', state: 'CA', zipCode: '90001' },
  onResourceSelect
}) => {
  const [resources, setResources] = useState<LocalResource[]>([]);
  const [filteredResources, setFilteredResources] = useState<LocalResource[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedCost, setSelectedCost] = useState<string>('all');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  // Sample data - in a real app, this would come from an API
  const sampleResources: LocalResource[] = [
    {
      id: '1',
      name: 'Los Angeles Public Library - Central Branch',
      type: 'government',
      description: 'Free library services including computer access, ESL classes, and citizenship resources',
      address: '630 W 5th St, Los Angeles, CA 90071',
      phone: '(213) 228-7000',
      website: 'https://www.lapl.org',
      hours: 'Mon-Thu: 10am-8pm, Fri-Sat: 9:30am-5:30pm, Sun: 1pm-5pm',
      rating: 4.5,
      distance: 0.8,
      languages: ['English', 'Spanish', 'Chinese', 'Korean'],
      services: ['ESL Classes', 'Computer Access', 'Citizenship Resources', 'Job Search Help'],
      eligibility: ['Open to all', 'Library card required for some services'],
      cost: 'free',
      isVerified: true,
      lastUpdated: new Date()
    },
    {
      id: '2',
      name: 'Catholic Charities of Los Angeles',
      type: 'nonprofit',
      description: 'Immigration services, food assistance, and family support programs',
      address: '1531 James M Wood Blvd, Los Angeles, CA 90015',
      phone: '(213) 251-3400',
      website: 'https://catholiccharitiesla.org',
      hours: 'Mon-Fri: 8am-5pm',
      rating: 4.8,
      distance: 1.2,
      languages: ['English', 'Spanish', 'Vietnamese', 'Tagalog'],
      services: ['Immigration Legal Services', 'Food Pantry', 'Family Counseling', 'Housing Assistance'],
      eligibility: ['Low-income families', 'Immigrants', 'Refugees'],
      cost: 'free',
      isVerified: true,
      lastUpdated: new Date()
    },
    {
      id: '3',
      name: 'Koreatown Immigrant Workers Alliance',
      type: 'nonprofit',
      description: 'Workers rights advocacy and community organizing for immigrant workers',
      address: '3465 W 8th St, Los Angeles, CA 90005',
      phone: '(213) 738-9050',
      website: 'https://kiwa.org',
      hours: 'Mon-Fri: 9am-6pm',
      rating: 4.7,
      distance: 2.1,
      languages: ['English', 'Korean', 'Spanish', 'Chinese'],
      services: ['Workers Rights', 'Wage Theft Recovery', 'Community Organizing', 'Legal Referrals'],
      eligibility: ['Immigrant workers', 'Low-wage workers'],
      cost: 'free',
      isVerified: true,
      lastUpdated: new Date()
    },
    {
      id: '4',
      name: 'LAUSD Adult Education Center',
      type: 'education',
      description: 'Adult education classes including ESL, GED, and vocational training',
      address: '1200 W 7th St, Los Angeles, CA 90017',
      phone: '(213) 241-3111',
      website: 'https://adulted.lausd.net',
      hours: 'Mon-Fri: 8am-8pm, Sat: 8am-2pm',
      rating: 4.3,
      distance: 1.5,
      languages: ['English', 'Spanish'],
      services: ['ESL Classes', 'GED Preparation', 'Vocational Training', 'Computer Skills'],
      eligibility: ['Adults 18+', 'Residents of LA County'],
      cost: 'low-cost',
      isVerified: true,
      lastUpdated: new Date()
    },
    {
      id: '5',
      name: 'Community Health Center of Los Angeles',
      type: 'healthcare',
      description: 'Comprehensive healthcare services for underserved communities',
      address: '7119 Sunset Blvd, Los Angeles, CA 90046',
      phone: '(323) 660-2450',
      website: 'https://chcla.org',
      hours: 'Mon-Fri: 8am-5pm, Sat: 8am-12pm',
      rating: 4.6,
      distance: 3.2,
      languages: ['English', 'Spanish', 'Armenian', 'Korean'],
      services: ['Primary Care', 'Dental Care', 'Mental Health', 'Women\'s Health'],
      eligibility: ['Low-income individuals', 'Uninsured', 'Underinsured'],
      cost: 'sliding-scale',
      isVerified: true,
      lastUpdated: new Date()
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setResources(sampleResources);
      setFilteredResources(sampleResources);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    filterResources();
  }, [searchTerm, selectedType, selectedCost, selectedLanguage, resources]);

  const filterResources = () => {
    let filtered = resources;

    if (searchTerm) {
      filtered = filtered.filter(resource =>
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.services.some(service => 
          service.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(resource => resource.type === selectedType);
    }

    if (selectedCost !== 'all') {
      filtered = filtered.filter(resource => resource.cost === selectedCost);
    }

    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(resource => 
        resource.languages.includes(selectedLanguage)
      );
    }

    setFilteredResources(filtered);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'government':
        return <MapPin className="w-5 h-5" />;
      case 'nonprofit':
        return <Heart className="w-5 h-5" />;
      case 'community':
        return <Users className="w-5 h-5" />;
      case 'business':
        return <Globe className="w-5 h-5" />;
      case 'healthcare':
        return <Heart className="w-5 h-5" />;
      case 'education':
        return <Globe className="w-5 h-5" />;
      default:
        return <MapPin className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'government':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'nonprofit':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'community':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'business':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'healthcare':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'education':
        return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getCostColor = (cost: string) => {
    switch (cost) {
      case 'free':
        return 'text-green-600 bg-green-100';
      case 'low-cost':
        return 'text-yellow-600 bg-yellow-100';
      case 'sliding-scale':
        return 'text-blue-600 bg-blue-100';
      case 'paid':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Local Resources Near You</h2>
        <p className="text-gray-600">
          Find helpful services and organizations in {userLocation.city}, {userLocation.state}
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for services, organizations, or keywords..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="government">Government</option>
              <option value="nonprofit">Nonprofit</option>
              <option value="community">Community</option>
              <option value="healthcare">Healthcare</option>
              <option value="education">Education</option>
            </select>

            <select
              value={selectedCost}
              onChange={(e) => setSelectedCost(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Costs</option>
              <option value="free">Free</option>
              <option value="low-cost">Low Cost</option>
              <option value="sliding-scale">Sliding Scale</option>
              <option value="paid">Paid</option>
            </select>

            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Languages</option>
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
              <option value="Chinese">Chinese</option>
              <option value="Korean">Korean</option>
              <option value="Vietnamese">Vietnamese</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            {filteredResources.length} Resources Found
          </h3>
          <div className="text-sm text-gray-600">
            Showing resources within 5 miles
          </div>
        </div>

        {filteredResources.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-700 mb-2">
              No resources found
            </h4>
            <p className="text-gray-600">
              Try adjusting your search terms or filters to find more resources.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredResources.map((resource) => (
              <div
                key={resource.id}
                className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-200"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getTypeColor(resource.type)}`}>
                      {getTypeIcon(resource.type)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">
                        {resource.name}
                      </h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{resource.distance} miles away</span>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          <span>{resource.rating}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCostColor(resource.cost)}`}>
                      {resource.cost.replace('-', ' ')}
                    </span>
                    {resource.isVerified && (
                      <span className="text-green-500 text-sm" title="Verified">
                        ✓
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {resource.description}
                </p>

                {/* Services */}
                <div className="mb-4">
                  <h5 className="font-medium text-gray-800 mb-2">Services Offered:</h5>
                  <div className="flex flex-wrap gap-2">
                    {resource.services.slice(0, 3).map((service, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                    {resource.services.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                        +{resource.services.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{resource.address}</span>
                  </div>
                  {resource.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${resource.phone}`} className="hover:text-primary-600">
                        {resource.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{resource.hours}</span>
                  </div>
                </div>

                {/* Languages */}
                <div className="mb-4">
                  <h5 className="font-medium text-gray-800 mb-2">Languages Spoken:</h5>
                  <div className="flex flex-wrap gap-2">
                    {resource.languages.map((language, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => onResourceSelect?.(resource)}
                    className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                  >
                    View Details
                  </button>
                  {resource.website && (
                    <a
                      href={resource.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Website
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocalResourceFinder;
